import {ChangeEventHandler, CSSProperties, FocusEventHandler, forwardRef, KeyboardEventHandler, useCallback, useImperativeHandle, useRef, useState} from 'react';
import './ValidatedInput.less';
import IconButton from "../IconButton/IconButton";
import Icons from "../IconButton/Icons";
import pwdTest from 'owasp-password-strength-test';
import * as DateUtil from '../../lib/dateutils';
import {InputAdornment, TextField} from "@mui/material";
import {Check, Email, ErrorOutlined, Password} from "@mui/icons-material";

export type InputValidationType = 'email' | 'password' | 'text' | 'date' | 'duration';

export type ValidatedInputProps = {
    // Passthroughs
    allowLastPass?: boolean; // Should we override the default logic and let lastpass modify the input?
    allowSubmit?: boolean; // Allow enter key to trigger form onSubmit
    asyncValidator?: boolean; // is the validator function asynchronous?
    autoComplete?: string; // is autocomplete allowed?
    autoFocus?: boolean; // Should this field be focused?
    className?: string; // additional classes for the className
    defaultValue?: string; // default value for the field
    disabled?: boolean; // is this field disabled?
    id?: string; // an ID to map to the field
    required?: boolean; // Is this field required?
    showErrorMessage?: boolean; // Should the error message be shown?
    showIcons?: boolean; // Should the icons be shown?
    showValidatedIcon?: boolean; // Should the validatedIcon be shown when applicable?
    tabIndex?: number; // If a specific tabIndex is required, put it here
    type: InputValidationType; // Kind of validation to do
    validatorFunc?: (value: string) => boolean | Promise<boolean>; // Custom validator function
    value?: string; // value for the field if using useValue

    // When using a TextField rather than an input
    label?: string; // label for the field (only used with useTextField)
    useTextField?: boolean; // Use a TextField instead of a input field
    textFieldProps?: object; // additional props for the TextField

    // Whitelists and blacklists
    blacklist?: string[]; // Don't allow values in this array
    whitelist?: string[]; // Don't allow values in this array

    // Message overrides
    blacklistedMessage?: string; // message to show if the item is blacklisted
    invalidValueMessage?: string; // Message to show if value is invalidated
    whitelistedMessage?: string; // message to show if the item is not in whitelist

    // Controlled or uncontrolled?
    useValue?: boolean; // use value instead of defaultValue

    // Callbacks
    onChange?: (value: string, isValid: boolean) => void; // Called on change
    onValid?: (value: string) => void; // function to call when the value goes from invalid to valid
    onInvalid?: (value: string) => void; // function to call when value goes from valid to invalid
};

export type ValidatorFn = (value: string) => boolean | Promise<boolean>;

export type ValidatedInputRef = {
    focus: () => void,
    clear: () => void
}

/**
 * Component to handle validated inputs, specifically text fields.
 *
 * Built-in support for passwords and emails.  Has lots of fun properties to handle things like whitelists, blacklists,
 * and custom validators.
 */
const ValidatedInput = forwardRef<ValidatedInputRef, ValidatedInputProps>((props, ref) => {
    const autoComplete = props.autoComplete || props.type === 'password' ? 'current-password' : 'off';
    const showErrorMessage = props.showErrorMessage || true;
    const showIcons = props.showIcons || true;
    const showValidatedIcon = props.showValidatedIcon || true;
    const useValue = props.useValue || false;

    const [showIcon, setShowIcon] = useState<boolean>(false);
    const [showPassword, setShowPassword] = useState<boolean>(false);
    const [valid, setValid] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string>('');

    const selfRef = useRef<HTMLInputElement>(null);
    const topRef = useRef<HTMLDivElement>(null);

    useImperativeHandle(ref, () => {
        return {
            focus() {
                selfRef.current.focus();
                selfRef.current.selectionStart = 0;
                selfRef.current.selectionEnd = selfRef.current.value.length;
            },
            clear() {
                setErrorMessage('');
                setValid(true);
            }
        }
    }, []);

    let validator: ValidatorFn;

    if (props.type === 'password') {
        pwdTest.config({
            allowPassphrases: true,
            maxLength: 128,
            minLength: 8,
            minPhraseLength: 16,
            minOptionalTestsToPass: 0
        });

        validator = props.validatorFunc || function (value) {
            const result = pwdTest.test(value);
            return result.errors.length === 0;
        };
    }
    else if (props.type === 'email') {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

        validator = props.validatorFunc || function (value) {
            return emailRegex.test(value);
        };
    }
    else if (props.type === 'date') {
        validator = props.validatorFunc || function (value) {
            return DateUtil.parseDate(value) !== null;
        };
    }
    else if (props.type === 'duration') {
        validator = props.validatorFunc || function (value) {
            return DateUtil.parseDuration(value) >= 0; // invalid durations parse as -1, not null!
        };
    }
    else {
        if (props.validatorFunc) validator = props.validatorFunc;
        else {
            validator = (() => true);
        }
    }

    /**
     * Handle key presses
     */
    const handleEnter: KeyboardEventHandler<HTMLInputElement> = (e) => {
        if (e.key === 'Enter') e.preventDefault();
    };

    const onBlur: FocusEventHandler<HTMLInputElement> = (e) => {
        onChange(e);
    };

    /**
     * @param e
     */
    const onChange: ChangeEventHandler<HTMLInputElement> = (e) => {
        e.preventDefault();
        e.stopPropagation();

        // Check if the field is valid
        const value = e.target.value.trim();

        let valid = true;
        let errorMessage = null;

        // Fail if a value is required but none given
        if (props.required && !value) {
            valid = false;
            errorMessage = 'Required';
        }

        // Check for specific value
        if (valid && props.whitelist) {
            valid = !!props.whitelist.find(item => item.toLowerCase().trim() === value.toLowerCase());
            if (!valid) errorMessage = props.whitelistedMessage || 'Not in list of allowable values';
        }

        // Check for blacklisted values
        if (valid && props.blacklist) {
            valid = !props.blacklist.find(item => item.toLowerCase().trim() === value.toLowerCase());
            if (!valid) errorMessage = props.blacklistedMessage || 'Blacklisted';
        }

        const _doWork = (_isValid: boolean, _errorMessage?: string) => {
            if (_isValid) {
                setShowIcon(!!value);
                setValid(_isValid);
                setErrorMessage(_errorMessage);

                if (props.onValid) props.onValid(value);
            }
            else if (!_isValid) {
                setShowIcon(!!value);
                setValid(_isValid);
                setErrorMessage(_errorMessage);
                if (props.onInvalid) props.onInvalid(value);
            }

            if (props.onChange) {
                props.onChange(value, _isValid);
            }
        };

        // Otherwise use the default or custom validator
        if (valid) {
            if (props.asyncValidator) {
                const f = props.validatorFunc(value);
                if (f instanceof Promise)
                    f.then(result => {
                        _doWork(result);
                    })
                        .catch(err => {
                            _doWork(false, err.message);
                        });
            }
            else {
                if (valid && props.required) valid = validator(value) as boolean;
                if (!valid) errorMessage = props.invalidValueMessage;

                _doWork(valid, errorMessage);
            }
        }
        else _doWork(false, errorMessage);
    };

    const onShowPassword = useCallback(() => {
        setShowPassword(!showPassword);
    }, [showPassword]);

    let type = props.type;
    let style: CSSProperties = {};

    if (type === 'date') {
        style.width = '6.5em !important';
        type = 'text';
    }
    else if (type === 'password' && showPassword) {
        type = 'text';
    }

    const ignoreLastPass = props.allowLastPass ? 'false' : props.type !== 'password' ? 'true' : 'false';

    let className = `validated-input ${props.className} ${props.disabled ? 'disabled' : ''}`;
    className += `${props.useTextField ? 'text-field' : ''}`.trim();

    return (
        <div className={className} tabIndex={-1} ref={topRef}>
            {props.useTextField &&
				<TextField id={props.id}
				           ref={selfRef}
				           autoComplete={autoComplete}
				           autoFocus={props.autoFocus}
				           data-lpignore={ignoreLastPass}
				           disabled={props.disabled}
				           error={!!errorMessage}
				           slotProps={{
                               input: {
                                   startAdornment: (props.type === 'email'
                                                    ? <InputAdornment position="start" tabIndex={-1}>
                                                        <Email style={{color: 'black'}} tabIndex={-1} fontSize='small'/>
                                                    </InputAdornment>
                                                    : props.type === 'password'
                                                      ? <InputAdornment position="start" tabIndex={-1}>
                                                          <Password style={{color: 'black'}} tabIndex={-1} fontSize='small'/>
                                                      </InputAdornment>
                                                      : null),
                                   endAdornment: (showValidatedIcon && (valid || errorMessage)
                                                  ? <InputAdornment position="end" tabIndex={-1}>
                                                      {valid
                                                       ? <Check style={{color: 'green'}} tabIndex={-1}/>
                                                       : <ErrorOutlined style={{color: 'red'}} tabIndex={-1}/>
                                                      }
                                                  </InputAdornment>
                                                  : null),
                                   tabIndex: props.tabIndex
                               }
                           }}
				           type={type}
				           label={props.label}
				           onChange={onChange}
				           onBlur={onBlur}
				           onKeyDown={props.allowSubmit ? undefined : handleEnter}
				           required={props.required}
				           sx={{width: '100%'}}
				           defaultValue={props.useValue ? props.value : props.defaultValue}
				           variant='standard'
                           {...props.textFieldProps}
				/>
            }

            {!props.useTextField && <div className="validated-input__content">
                {useValue
                 ? <input id={props.id}
                          ref={selfRef}
                          autoComplete={autoComplete}
                          autoFocus={props.autoFocus}
                          data-lpignore={ignoreLastPass}
                          disabled={props.disabled}
                          onBlur={onBlur}
                          onChange={onChange}
                          onKeyDown={props.allowSubmit ? undefined : handleEnter}
                          required={props.required}
                          style={style}
                          tabIndex={props.tabIndex}
                          type={type}
                          value={props.defaultValue}
                 />
                 : <input id={props.id}
                          ref={selfRef}
                          autoComplete={autoComplete}
                          autoFocus={props.autoFocus}
                          data-lpignore={ignoreLastPass}
                          defaultValue={props.defaultValue}
                          disabled={props.disabled}
                          onBlur={onBlur}
                          onChange={onChange}
                          onKeyDown={props.allowSubmit ? undefined : handleEnter}
                          required={props.required}
                          style={style}
                          tabIndex={0}
                          type={type}
                 />
                }
			</div>
            }

            {/* Show Password button */}
            {props.type === 'password' && showIcons && !props.disabled
             ? <IconButton id='show-password' type={Icons.VIEW} onClick={onShowPassword} tabIndex={-1}/>
             : null
            }

            {/* (In)valid icon */}
            {showIcon && showIcons && !props.useTextField
             ? valid
               ? (showValidatedIcon ? <IconButton type={Icons.CHECKMARK} color="darkgreen" tabIndex={-1}/> : null)
               : <IconButton type={Icons.ERROR} color="maroon" tabIndex={-1}/>
             : null
            }

            {showErrorMessage && errorMessage ? <div className="ErrorMessage">{errorMessage}</div> : null}
        </div>
    );
});

ValidatedInput.displayName = 'ValidatedInput';

export default ValidatedInput;
